import { useEffect, useMemo } from "react";
import BigNumber from "bignumber.js";
import { useSettings } from "../contexts/SettingsContext";
import {
  useRetirementStore,
  calculateRetirement,
} from "../store/retirementStore";
import type {
  RetirementData,
  RetirementResult,
} from "../store/retirementStore";
import type { AssetBreakdown } from "../types/projection";
import { buildProjectionRows } from "../utils/projectionRows";
import { createLogger } from "../utils/logger";

interface UseProjectionsReturn {
  data: RetirementData | null;
  result: RetirementResult | null;
  /** Net worth at retirement age from the detailed year-by-year projection */
  netWorthAtRetirement: number;
  /** Liquid assets (RRSP, TFSA, investments, cash) at retirement */
  liquidAssetsAtRetirement: number;
  /** Total outstanding liabilities at retirement */
  liabilitiesAtRetirement: number;
  totals: {
    assets: number;
    liabilities: number;
    income: number;
    expenses: number;
  } | null;
  assetBreakdown: AssetBreakdown | null;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook to calculate retirement projections from SettingsContext.
 *
 * This hook consumes the centralised settings from SettingsContext (via
 * useSettings) instead of independently fetching settings from Firestore.
 * This eliminates duplicate fetching, avoids the race-condition that arose
 * from reading Zustand state immediately after setData/calculate, and
 * removes ~100 lines of redundant mapping code (F5 consolidation).
 *
 * @param userId - The authenticated user's ID (used for logging only)
 * @returns {UseProjectionsReturn} Retirement data, calculations, and state
 */
export function useProjections(
  userId: string | undefined
): UseProjectionsReturn {
  const projectionsLogger = useMemo(
    () => createLogger({ prefix: "useProjections" }),
    []
  );
  const {
    settings,
    loading: settingsLoading,
    error: settingsError,
    statisticsInitialized,
  } = useSettings();
  const { setData: setStoreData } = useRetirementStore();

  // Derive retirement data from settings
  // CRITICAL: Wait for statistics to be initialized before calculating projections.
  // This prevents showing $0 values when statistics haven't loaded yet and user data
  // falls back to statistics defaults (which return 0 when statistics aren't loaded).
  const retirementData = useMemo<RetirementData | null>(() => {
    if (!settings) return null;

    const {
      profile,
      investableAssets,
      realEstateAssets,
      liabilities,
      income,
      expenses,
      assumptions,
    } = settings;
    const currentAge = profile.currentAge;

    // Monthly contribution = income - expenses (but never negative)
    const monthlyIncome = income.monthlyIncome;
    const monthlyExpenses = expenses.monthlyExpenses;
    const monthlyContribution = BigNumber.max(
      0,
      new BigNumber(monthlyIncome).minus(monthlyExpenses)
    ).toNumber();

    // Net worth: assets minus liabilities (can be negative)
    const totalAssets = new BigNumber(investableAssets.totalInvestableAssets)
      .plus(realEstateAssets.totalRealEstateAssets)
      .toNumber();
    const netWorth = new BigNumber(totalAssets)
      .minus(liabilities.totalLiabilities)
      .toNumber();

    return {
      currentAge,
      retirementAge: assumptions.retirementAge,
      lifeExpectancy: assumptions.lifeExpectancy,
      currentSavings: netWorth,
      monthlyContribution,
      annualReturn: assumptions.expectedReturnRate,
      inflationRate: assumptions.inflationRate,
      monthlyExpenses,
      expensesAtRetirement: assumptions.expensesAtRetirement,
      currency: "CAD",
      debts: {
        mortgage: {
          balance: liabilities.mortgage,
          interestRate: liabilities.mortgageInterestRate,
          monthlyPayment: expenses.mortgagePayment,
        },
        creditCard: {
          balance: liabilities.creditCardBalance,
          interestRate: liabilities.creditCardInterestRate,
          monthlyPayment: expenses.creditCardPayment,
        },
        carLoan: {
          balance: liabilities.carLoan,
          interestRate: liabilities.carLoanInterestRate,
          monthlyPayment: expenses.carLoanPayment,
        },
        lineOfCredit: {
          balance: liabilities.lineOfCredit,
          interestRate: liabilities.lineOfCreditInterestRate,
          monthlyPayment: expenses.lineOfCreditPayment,
        },
        studentLoan: {
          balance: liabilities.studentLoans,
          interestRate: liabilities.studentLoanInterestRate,
          monthlyPayment: expenses.studentLoanPayment,
        },
        otherLiabilities: {
          balance: liabilities.otherLiabilities,
          interestRate: liabilities.otherLiabilitiesInterestRate,
          monthlyPayment: expenses.otherLiabilitiesPayment,
        },
      },
    };
  }, [settings]);

  // Derive totals from settings
  const totals = useMemo(() => {
    if (!settings) return null;
    return {
      assets: new BigNumber(settings.investableAssets.totalInvestableAssets)
        .plus(settings.realEstateAssets.totalRealEstateAssets)
        .toNumber(),
      liabilities: settings.liabilities.totalLiabilities,
      income: settings.income.monthlyIncome,
      expenses: settings.expenses.monthlyExpenses,
    };
  }, [settings]);

  // Derive asset breakdown for detailed projections
  const assetBreakdown = useMemo<AssetBreakdown | null>(() => {
    if (!settings) return null;
    const { investableAssets, realEstateAssets, assumptions } = settings;
    return {
      liquidAssets: new BigNumber(investableAssets.rrspBalance)
        .plus(investableAssets.tfsaBalance)
        .plus(investableAssets.fhsaBalance)
        .plus(investableAssets.respBalance)
        .plus(investableAssets.nonRegisteredInvestments)
        .plus(investableAssets.cashSavings)
        .toNumber(),
      emergencyFund: investableAssets.emergencyFund,
      realEstateAssets: new BigNumber(realEstateAssets.primaryResidence)
        .plus(realEstateAssets.investmentProperties)
        .toNumber(),
      otherAppreciableAssets: realEstateAssets.otherAssets,
      depreciatingAssets: realEstateAssets.depreciatingAssets,
      realEstateAppreciationRate: assumptions.realEstateAppreciationRate,
      otherAssetGrowthRate: assumptions.otherAssetGrowthRate,
      depreciationRate: assumptions.depreciationRate,
    };
  }, [settings]);

  // Calculate projections when retirement data changes
  const result = useMemo<RetirementResult | null>(() => {
    if (!retirementData) return null;
    return calculateRetirement(retirementData);
  }, [retirementData]);

  // Compute key figures at retirement from the detailed year-by-year projection.
  // These are the authoritative figures for the hero card because they account for
  // different asset growth rates (liquid vs real-estate vs depreciable).
  const retirementRowData = useMemo<{
    netWorth: number;
    liquidAssets: number;
    liabilities: number;
  }>(() => {
    if (!retirementData)
      return { netWorth: 0, liquidAssets: 0, liabilities: 0 };
    try {
      const rows = buildProjectionRows(
        retirementData,
        assetBreakdown ?? undefined
      );
      // Find the last working-year row (the year just before retirement)
      const retirementRow = [...rows]
        .reverse()
        .find((r) => r.phase === "working");
      if (retirementRow) {
        return {
          netWorth: retirementRow.netWorth,
          liquidAssets: retirementRow.liquidAssets,
          liabilities: retirementRow.totalDebt,
        };
      }
      const firstRow = rows[0];
      return firstRow
        ? {
            netWorth: firstRow.netWorth,
            liquidAssets: firstRow.liquidAssets,
            liabilities: firstRow.totalDebt,
          }
        : { netWorth: 0, liquidAssets: 0, liabilities: 0 };
    } catch (error) {
      projectionsLogger.warn("Failed to extract retirement row data", {
        error,
      });
      return { netWorth: 0, liquidAssets: 0, liabilities: 0 };
    }
  }, [retirementData, assetBreakdown, projectionsLogger]);

  const netWorthAtRetirement = retirementRowData.netWorth;
  const liquidAssetsAtRetirement = retirementRowData.liquidAssets;
  const liabilitiesAtRetirement = retirementRowData.liabilities;

  // Sync retirement data into the Zustand store so other consumers
  // (e.g. PortfolioChart) can read from it.
  useEffect(() => {
    if (retirementData) {
      setStoreData(retirementData);
      projectionsLogger.debug("Projection calculation completed", {
        userId,
        finalAmount: result?.finalAmount,
        readinessScore: result?.readinessScore,
      });
    }
  }, [retirementData, setStoreData, userId, result, projectionsLogger]);

  // Derive loading & error
  // Show loading state when settings are loading OR statistics aren't initialized yet
  const loading = settingsLoading || !statisticsInitialized;
  const error = settingsError;

  return {
    data: retirementData,
    result,
    netWorthAtRetirement,
    liquidAssetsAtRetirement,
    liabilitiesAtRetirement,
    totals,
    assetBreakdown,
    loading,
    error,
  };
}
